/**
 * Async ELO persistence after score submit (interim until server-side batch ELO).
 */

import { supabase } from '../utils/supabase';

const persistedGameIds = new Set();

export function resetScoreEloPipelineForTests() {
  persistedGameIds.clear();
}

export function isGameEloPersisted(gameId) {
  return gameId != null && persistedGameIds.has(gameId);
}

/**
 * @param {object} params
 * @param {string} params.gameId
 * @param {object[]} params.eloHistoryRecords
 * @param {boolean} [params.testMode]
 * @param {(msg: string) => void} [params.onError]
 * @param {(updates: Record<string, object>)} [params.onSuccess]
 */
export async function persistScoreEloAsync({
  gameId,
  eloHistoryRecords,
  testMode = false,
  onError,
  onSuccess,
  preGameElos = {},
}) {
  if (!gameId || !Array.isArray(eloHistoryRecords) || eloHistoryRecords.length === 0) {
    return;
  }
  if (persistedGameIds.has(gameId)) return;

  const run = async () => {
    const payload = eloRecordsToServerPayload(eloHistoryRecords);
    const { data, error } = await supabase.rpc('apply_session_game_elo_batch', {
      p_session_game_id: gameId,
      p_updates: payload,
    });

    if (error) {
      throw error;
    }

    if (!data?.ok) {
      throw new Error(data?.code || 'ELO batch apply failed');
    }

    const eloDeltasFromRpc = {};
    const deltas = data.elo_deltas || {};
    for (const record of eloHistoryRecords) {
      const d = deltas[record.player_id];
      if (d && typeof d.total === 'number') {
        eloDeltasFromRpc[record.player_id] = Math.round(d.total);
      } else if (preGameElos[record.player_id] !== undefined && record.new_value !== undefined) {
        eloDeltasFromRpc[record.player_id] = Math.round(
          record.new_value - preGameElos[record.player_id]
        );
      } else {
        eloDeltasFromRpc[record.player_id] = 0;
      }
    }

    if (testMode) {
      console.log('[scoreEloPipeline] apply_session_game_elo_batch ok', data);
    }

    persistedGameIds.add(gameId);
    onSuccess?.(eloDeltasFromRpc);
  };

  try {
    await run();
  } catch (firstErr) {
    try {
      await run();
    } catch (retryErr) {
      console.error('[scoreEloPipeline] ELO persist failed after retry:', retryErr);
      onError?.(
        'Score saved; ELO update failed — refresh or contact the host.',
        firstErr
      );
    }
  }
}

/**
 * Build JSON-serializable payload for server batch ELO RPC.
 * @param {object[]} records — from createFinalEloHistoryRecords
 */
export function eloRecordsToServerPayload(records) {
  if (!Array.isArray(records)) return [];
  return records.map((r) => ({
    player_id: r.player_id,
    club_id: r.club_id,
    session_id: r.session_id,
    season_id: r.season_id,
    pair_type: r.pair_type,
    game_type_id: r.game_type_id,
    new_value: r.new_value,
  }));
}
